import { useState } from 'react'
import type { ReactElement } from 'react'

import { Button, InputText } from '../../components'
import { getErrorCode } from '../../lib/errorCode'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import styles from './ChangePasswordPage.module.scss'

/** Four digits, as `pinSchema` in the API. */
const PIN = /^\d{4}$/

/**
 * A wrong (or missing) current password comes back as UNAUTHORIZED, the code of
 * a dead session, and the session is alive: as on the password screen, the
 * generic copy would be a lie. A PIN session is refused (FORBIDDEN) whatever
 * it sends: it only opens almacén.
 */
const changePinError = (error: unknown): string => {
  const code = getErrorCode(error)
  if (code === 'UNAUTHORIZED') {
    return 'La contraseña actual no es correcta. Si tu cuenta tiene contraseña, escríbela.'
  }
  if (code === 'FORBIDDEN') {
    return 'Con el PIN solo se entra en almacén: para cambiar tu cuenta, entra con tu contraseña o con Google.'
  }
  return trpcMessage(error)
}

/**
 * Changing one's own PIN for almacén's tablet. The API asks the current
 * password only of an account that has one, and the session does not say
 * which, so the field is always there and may be left empty: the server
 * decides.
 */
export const ChangePinPage = (): ReactElement => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [pin, setPin] = useState('')
  const [repeated, setRepeated] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const changePin = trpc.auth.changePin.useMutation({
    onSuccess: () => {
      setCurrentPassword('')
      setPin('')
      setRepeated('')
      setDone(true)
    },
    onError: (error) => setErrorMessage(changePinError(error)),
  })

  const mismatch = repeated !== '' && repeated !== pin

  return (
    <section className={styles['change-password-page']}>
      <h1 className={styles['change-password-page__title']}>Cambiar PIN</h1>

      <form
        className={styles['change-password-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          setErrorMessage(null)
          setDone(false)
          changePin.mutate({ pin, ...(currentPassword === '' ? {} : { currentPassword }) })
        }}
      >
        <InputText
          label="Contraseña actual"
          type="password"
          autoComplete="current-password"
          hint="Déjala vacía si entras solo con Google."
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
        <InputText
          label="PIN nuevo"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          hint="Cuatro dígitos: con ellos entras en la tablet de almacén."
          value={pin}
          onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
          required
        />
        <InputText
          label="Repite el PIN"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          value={repeated}
          onChange={(event) => setRepeated(event.target.value.replace(/\D/g, ''))}
          required
        />

        {mismatch && (
          <p role="alert" className={styles['change-password-page__error']}>Los dos PIN no coinciden.</p>
        )}
        {errorMessage !== null && (
          <p role="alert" className={styles['change-password-page__error']}>{errorMessage}</p>
        )}
        {done && (
          <p role="status" className={styles['change-password-page__ok']}>PIN cambiado.</p>
        )}

        <Button
          type="submit"
          size="lg"
          fullWidth
          disabled={changePin.isPending || !PIN.test(pin) || repeated !== pin}
          label={changePin.isPending ? 'Guardando…' : 'Guardar'}
        />
      </form>
    </section>
  )
}
